// src/components/EventsCalendarPage.js
import axios from "../utils/axiosConfig"; // runs the 401/403 logout logic globally
import React, { useState, useEffect, useMemo } from "react";
import "../Style/Calendar.css";
import {
  buildGoogleCalendarUrl,
  buildOutlookCalendarUrl,
  buildYahooCalendarUrl,
  downloadICS,
  eventHasValidDates,
} from "../utils/calendarUtils";

const weekdayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const FALLBACK_EVENTS = [
  {
    id: "venture-summit-2026",
    date: "2026-05-18",
    endDate: "2026-05-22",
    time: "",
    title: "2026 KPMG Africa Private Enterprise Venture Summit",
    location: "JW Marriott Hotel, Nairobi, Kenya",
    description:
      "Africa Accelerated — powering Africa's engines of growth. The third edition of KPMG Private Enterprise's flagship summit.",
    url: "https://kpmg.com/ke/en/insights/2026/05/2026-kpmg-africa-private-enterprise-venture-summit.html",
  },
];

function buildCalendarMatrix(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const weeks = [];
  let current = 1 - firstDay;
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++, current++) {
      week.push(current < 1 || current > daysInMonth ? null : current);
    }
    weeks.push(week);
  }
  return weeks;
}

// Build event → calendar-utils shape
function toCalendarPayload(ev) {
  return {
    id: ev.id,
    title: ev.title,
    description: ev.description || "",
    location: ev.location || "",
    start: ev.date,
    end: ev.endDate || ev.date,
    startTime: ev.time || undefined,
    endTime: ev.endTime || undefined,
  };
}

export default function EventsCalendarPage() {
  const today = new Date();
  const [viewDate, setViewDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const [events, setEvents] = useState(FALLBACK_EVENTS);
  const [selected, setSelected] = useState(null);

  const yearNum = viewDate.getFullYear();
  const monthNum = viewDate.getMonth();
  const weeks = useMemo(
    () => buildCalendarMatrix(yearNum, monthNum),
    [yearNum, monthNum]
  );
  const displayMonthYear = viewDate.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const token = localStorage.getItem("token");
        const resp = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/events/`,
          { headers: { Authorization: `Token ${token}` } }
        );
        if (Array.isArray(resp.data) && resp.data.length > 0) {
          setEvents(resp.data);
        }
      } catch (err) {
        console.error("Failed to load events:", err);
      }
    };
    fetchEvents();
  }, []);

  const eventsByDay = (day) => {
    const key = `${yearNum}-${String(monthNum + 1).padStart(2, "0")}-${String(
      day
    ).padStart(2, "0")}`;
    return events.filter((e) => {
      // Single-day event matches `e.date`
      if (e.date === key) return true;
      // Multi-day event spans `e.date` → `e.endDate`
      if (e.endDate && e.date <= key && key <= e.endDate) return true;
      return false;
    });
  };

  const handlePrev = () =>
    setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const handleNext = () =>
    setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const goToToday = () =>
    setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));

  const upcoming = useMemo(() => {
    const todayKey = today.toISOString().slice(0, 10);
    return events
      .filter((e) => (e.endDate || e.date) >= todayKey)
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, 5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  return (
    <div className="calendar-page">
      <header className="calendar-header">
        <span className="calendar-eyebrow">Events</span>
        <h2>KPMG Africa Private Enterprise Events Calendar</h2>
        <p className="calendar-lede">
          Browse upcoming and past events. Click any event to add it to your
          personal calendar.
        </p>
      </header>

      <div className="calendar-toolbar">
        <div className="calendar-nav">
          <button onClick={handlePrev} aria-label="Previous month">‹</button>
          <span>{displayMonthYear}</span>
          <button onClick={handleNext} aria-label="Next month">›</button>
        </div>
        <button className="calendar-today-btn" onClick={goToToday}>
          Today
        </button>
      </div>

      <div className="calendar-layout">
        <div className="calendar-main">
          <table className="calendar-grid">
            <thead>
              <tr>
                {weekdayNames.map((d, i) => (
                  <th key={i}>{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, wIdx) => (
                <tr key={wIdx}>
                  {week.map((day, dIdx) => {
                    const isToday =
                      day === today.getDate() &&
                      monthNum === today.getMonth() &&
                      yearNum === today.getFullYear();
                    const dayEvents = day ? eventsByDay(day) : [];
                    return (
                      <td
                        key={dIdx}
                        className={`${isToday ? "today" : ""} ${
                          day && dayEvents.length ? "has-events" : ""
                        } ${!day ? "empty" : ""}`}
                      >
                        {day && <div className="day-number">{day}</div>}
                        {dayEvents.map((ev) => (
                          <button
                            key={ev.id}
                            className="calendar-item"
                            onClick={() => setSelected(ev)}
                          >
                            {ev.time && (
                              <span className="calendar-item-time">
                                {ev.time}
                              </span>
                            )}
                            <span className="calendar-item-title">
                              {ev.title}
                            </span>
                          </button>
                        ))}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <aside className="calendar-side">
          <h3 className="calendar-side-title">Upcoming</h3>
          {upcoming.length === 0 ? (
            <p className="calendar-side-empty">No upcoming events listed.</p>
          ) : (
            upcoming.map((ev) => (
              <button
                key={ev.id}
                className="calendar-side-card"
                onClick={() => setSelected(ev)}
              >
                <div className="calendar-side-date">
                  {formatPretty(ev.date)}
                  {ev.endDate && ev.endDate !== ev.date
                    ? ` – ${formatPretty(ev.endDate)}`
                    : ""}
                </div>
                <div className="calendar-side-title-row">{ev.title}</div>
                {ev.location && (
                  <div className="calendar-side-loc">{ev.location}</div>
                )}
              </button>
            ))
          )}
        </aside>
      </div>

      {selected && (
        <EventDetailsModal event={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function formatPretty(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function EventDetailsModal({ event, onClose }) {
  const payload = toCalendarPayload(event);
  const valid = eventHasValidDates(payload);
  const googleUrl = valid && buildGoogleCalendarUrl(payload);
  const outlookUrl = valid && buildOutlookCalendarUrl(payload);
  const yahooUrl = valid && buildYahooCalendarUrl(payload);

  return (
    <div className="calendar-modal-backdrop" onClick={onClose}>
      <div
        className="calendar-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="calendar-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h3>{event.title}</h3>
        <p className="calendar-modal-meta">
          {formatPretty(event.date)}
          {event.endDate && event.endDate !== event.date
            ? ` – ${formatPretty(event.endDate)}`
            : ""}
          {event.time ? ` · ${event.time}` : ""}
        </p>
        {event.location && (
          <p className="calendar-modal-loc">📍 {event.location}</p>
        )}
        {event.description && (
          <p className="calendar-modal-desc">{event.description}</p>
        )}

        {valid ? (
          <div className="calendar-modal-actions">
            <span className="calendar-buttons-title">Add to calendar</span>
            <div className="calendar-buttons-row">
              <a
                href={googleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="calendar-btn google"
              >
                Google
              </a>
              <a
                href={outlookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="calendar-btn outlook"
              >
                Outlook
              </a>
              <a
                href={yahooUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="calendar-btn yahoo"
              >
                Yahoo
              </a>
              <button
                onClick={() => downloadICS(payload)}
                className="calendar-btn ics"
              >
                Apple / .ics
              </button>
            </div>
          </div>
        ) : (
          <p className="event-hint">Event date is to be confirmed.</p>
        )}

        {event.url && (
          <a
            href={event.url}
            target="_blank"
            rel="noopener noreferrer"
            className="calendar-modal-link"
          >
            View event details →
          </a>
        )}
      </div>
    </div>
  );
}
